import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import { OfflineIndicator } from "./components/OfflineIndicator";
import { InstallPrompt } from "./components/InstallPrompt";
import { SkipNav } from "./components/ui/skip-nav";
import PageSkeleton from "./components/PageSkeleton";
import { AnalyticsProvider } from "./components/AnalyticsProvider";
import { userBehaviorTracker } from "./lib/analytics/userBehavior";
import { logger } from "@/lib/logger";
import { supabase } from "@/integrations/supabase/client";
import { initPerformanceOptimizations } from "@/lib/performanceInit";

// Lazy load route components for better performance
const Index = lazy(() => import("./pages/Index"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Auth = lazy(() => import("./pages/Auth"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const AuthReset = lazy(() => import("./pages/AuthReset"));
const Settings = lazy(() => import("./pages/Settings"));
const History = lazy(() => import("./pages/History"));
const Profile = lazy(() => import("./pages/Profile"));
const ProfileRedirect = lazy(() => import("./pages/ProfileRedirect"));
const Accounts = lazy(() => import("./pages/Accounts"));
const Tracking = lazy(() => import("./pages/Tracking"));
const Journal = lazy(() => import("./pages/Journal"));
const Checklist = lazy(() => import("./pages/Checklist"));
const Insights = lazy(() => import("./pages/Insights"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Alerts = lazy(() => import("./pages/Alerts"));
const TraderGlobe = lazy(() => import("./pages/TraderGlobe"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AppShell = lazy(() => import("./components/AppShell"));

// Admin routes - lazy loaded with named exports
const AdminLayout = lazy(() => import("./components/admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const JournalIntelligence = lazy(() => import("./pages/admin/JournalIntelligence"));
const AuditLogs = lazy(() => import("./pages/admin/AuditLogs"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const TraderBlaidWaitlist = lazy(() => import("./pages/admin/TraderBlaidWaitlist"));
const EmailTesting = lazy(() => import("./pages/admin/EmailTesting"));
const PropFirmRulesAdmin = lazy(() => import("./pages/admin/PropFirmRulesAdmin"));
const NinjaTraderImport = lazy(() => import("./pages/brokerage/NinjaTraderImport"));
const TradovateCallback = lazy(() => import("./pages/TradovateCallback"));
const TradovateReconnect = lazy(() => import("./pages/TradovateReconnect"));
const OAuthCallback = lazy(() => import("./pages/OAuthCallback"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Changelog = lazy(() => import("./pages/Changelog"));
const AIAutomations = lazy(() => import("./pages/AIAutomations"));
const DataHealth = lazy(() => import("./pages/DataHealth"));
const ParityFinalizer = lazy(() => import("./pages/ParityFinalizer"));
const ParityVerification = lazy(() => import("./pages/ParityVerification"));
import { useMobileSetup } from "./hooks/useMobileSetup";
import { useDailyPulseNotifications } from "./hooks/useDailyPulseNotifications";
import { useVersionCheck } from "./hooks/useVersionCheck";
import { DevSubscriptionPanel } from "./components/DevSubscriptionPanel";
import { useBootstrap } from "./hooks/useBootstrap";
import { buildRemoteUrl } from "./lib/native/bootstrap";
import Offline from "./components/native/Offline";
import Maintenance from "./components/native/Maintenance";
import UpdateRequired from "./components/native/UpdateRequired";
import { AccountProvider } from "./contexts/AccountContext";
import { SkinProvider } from "./contexts/SkinContext";
import { DemoModeProvider } from "./contexts/DemoModeContext";
import { Capacitor } from '@capacitor/core';
import AppScreen from "./components/layout/AppScreen";
import { App as CapacitorApp } from '@capacitor/app';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10, // 10 minutes - data stays fresh longer
      gcTime: 1000 * 60 * 60, // 1 hour - longer cache retention
      retry: 2, // Retry failed requests twice
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnReconnect: true, // Refetch when reconnecting
      refetchOnMount: false, // Don't refetch if data exists in cache
    },
    mutations: {
      retry: 1,
    },
  },
});

// ✅ Register deep link handler BEFORE React renders (race-free)
if (Capacitor.isNativePlatform()) {
  CapacitorApp.addListener('appUrlOpen', ({ url }) => {
    logger.info('App', 'Deep link received (global)', { url });
    
    if (url.includes('oauth/callback')) {
      // Extract query string from deep link
      const urlObj = new URL(url);
      const code = urlObj.searchParams.get('code');
      const state = urlObj.searchParams.get('state');
      
      if (code && state) {
        // Delegate to SPA route so the same verifier/state logic runs
        const queryString = `?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;
        window.location.href = `/oauth/callback${queryString}`;
      }
    }
  });
}

const AppContent = () => {
  const [userId, setUserId] = useState<string | undefined>();
  
  useMobileSetup();
  useDailyPulseNotifications(userId);
  useVersionCheck();
  const bootstrapState = useBootstrap();

  // Initialize performance optimizations once
  useEffect(() => {
    initPerformanceOptimizations();
  }, []);

  // Get current user for notification management
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserId(data.user?.id);
    });

    const { data: authListener } = supabase.auth.onAuthStateChange((_, session) => {
      setUserId(session?.user?.id);
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);
  
  // Prefetch critical routes after auth for instant navigation
  useEffect(() => {
    if (userId) {
      import('@/lib/prefetch').then(({ prefetchRoutes }) => {
        prefetchRoutes();
      });
    }
  }, [userId]);
  
  // Native iOS pull-to-refresh listener
  useEffect(() => {
    const onNativeRefresh = async () => {
      // Central refresh action (invalidate queries, refetch, etc.)
      await queryClient.invalidateQueries();
      
      // Show success animation (import hapticSuccess from '@/lib/haptics')
      const { hapticSuccess } = await import('@/lib/haptics');
      hapticSuccess();
      
      window.location.reload();
    };
    window.addEventListener('native:refresh', onNativeRefresh);
    return () => window.removeEventListener('native:refresh', onNativeRefresh);
  }, []);
  
  // Track page views
  useEffect(() => {
    const path = window.location.pathname + window.location.hash;
    userBehaviorTracker.trackPageView(path);
  }, []);
  
  // Log remote URL configuration when bootstrap succeeds
  useEffect(() => {
    if (bootstrapState.status === 'ok' && Capacitor.isNativePlatform()) {
      const remoteUrl = buildRemoteUrl(
        import.meta.env.VITE_ENV === 'staging' ? 'https://staging.traderblaid.app' : 'https://traderblaid.app',
        bootstrapState.manifest
      );
      logger.debug('App', 'Remote URL configured', { remoteUrl });
    }
  }, [bootstrapState]);
  
  // Bootstrap state handling - gate app loading AFTER all hooks
  if (bootstrapState.status === 'loading') {
    return <PageSkeleton />;
  }
  
  if (bootstrapState.status === 'offline') {
    return <Offline />;
  }
  
  if (bootstrapState.status === 'maintenance') {
    return <Maintenance message={bootstrapState.message} />;
  }
  
  if (bootstrapState.status === 'killed') {
    return <Maintenance message={bootstrapState.message || 'Service temporarily unavailable.'} />;
  }
  
  if (bootstrapState.status === 'update_required') {
    return <UpdateRequired minVersion={bootstrapState.minVersion} message={bootstrapState.message} />;
  }
  
  if (bootstrapState.status === 'error') {
    return <Maintenance message={`Error: ${bootstrapState.error}`} />;
  }
  
  return (
    <>
      <SkipNav />
      <OfflineIndicator />
      <InstallPrompt />
      <DevSubscriptionPanel />
      <Suspense fallback={<PageSkeleton />}>
        <Routes>
        <Route path="/" element={<Auth />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/reset" element={<AuthReset />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/app" element={<AppShell />}>
          <Route index element={<Index />} />
          <Route path="accounts" element={<Accounts />} />
          <Route path="tracking" element={<Tracking />} />
          <Route path="journal" element={<Journal />} />
          <Route path="checklist" element={<Checklist />} />
          <Route path="profile" element={<Profile />} />
        </Route>
        <Route path="/settings" element={<Settings />} />
        <Route path="/history" element={<History />} />
        <Route path="/profile" element={<ProfileRedirect />} />
        <Route path="/profile/:username" element={<Profile />} />
        <Route path="/insights" element={<Insights />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/alerts" element={<Alerts />} />
        <Route path="/globe" element={<TraderGlobe />} />
        <Route path="/automations" element={<AIAutomations />} />
        <Route path="/data-health" element={<DataHealth />} />
        <Route path="/parity-finalizer" element={<ParityFinalizer />} />
        <Route path="/parity-verification" element={<ParityVerification />} />
        <Route path="/oauth/callback" element={<OAuthCallback />} />
        <Route path="/tradovate/callback" element={<TradovateCallback />} />
        <Route path="/tradovate/reconnect" element={<TradovateReconnect />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/changelog" element={<Changelog />} />
        
        {/* Admin Routes */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="journal-intelligence" element={<JournalIntelligence />} />
          <Route path="audit" element={<AuditLogs />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="traderblaid" element={<TraderBlaidWaitlist />} />
          <Route path="email-testing" element={<EmailTesting />} />
          <Route path="prop-firm-rules" element={<PropFirmRulesAdmin />} />
        </Route>
        
        {/* Brokerage Routes */}
        <Route path="/brokerage/ninjatrader/import" element={<NinjaTraderImport />} />
        
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <DemoModeProvider>
      <SkinProvider>
        <TooltipProvider>
          <AppScreen>
            <div className="w-full max-w-[100vw] overflow-x-hidden">
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <AnalyticsProvider>
                  <AccountProvider>
                    <AppContent />
                  </AccountProvider>
                </AnalyticsProvider>
              </BrowserRouter>
            </div>
          </AppScreen>
        </TooltipProvider>
      </SkinProvider>
    </DemoModeProvider>
  </QueryClientProvider>
);

export default App;
